import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * A card's grip in edit mode, in the gap to the left of the card, level with its title.
 *
 * The only control a card carries. Its settings open by clicking the card itself, or tabbing into
 * it -- see the deck's openBlockEditor -- so there is no pen beside the grip: two buttons on every
 * card was a lot of chrome for a deck that is mostly being looked at. Removing a module is in the
 * side panel, beside the settings of the module it removes.
 *
 * Dragged, the grip moves the card to wherever it is dropped on the page -- a half by itself, beside
 * another half or to the other side of its row; a full-width card into the empty side of a half,
 * which makes it half width -- or into another subsection when dropped on that subsection's name in
 * the index. Focused, the up and down arrow keys move the row one place, and on a half-width card
 * left and right put it on that side of its row: a drag is a pointer-only gesture. The deck does
 * the moving; this only reports what was asked for.
 */
@Component({
  selector: 'app-report-block-actions',
  standalone: false,
  template: `
    <!-- Not a <button>: Firefox will not start a drag from one. A focusable span instead, carrying
         the button role and taking the arrow keys, with how to use it said to a screen reader. -->
    <span class="block-grip d-inline-flex align-items-center justify-content-center"
          role="button"
          tabindex="0"
          draggable="true"
          tooltip="Drag to move"
          [attr.data-grip]="blockId"
          [attr.aria-label]="'Move ' + label"
          aria-describedby="block-grip-help"
          (dragstart)="dragStart.emit($event)"
          (dragend)="dragEnd.emit()"
          (keydown.arrowup)="$event.preventDefault(); canMoveUp ? move.emit(-1) : null"
          (keydown.arrowdown)="$event.preventDefault(); canMoveDown ? move.emit(1) : null"
          (keydown.arrowleft)="half ? $event.preventDefault() : null; half ? sideMove.emit('left') : null"
          (keydown.arrowright)="half ? $event.preventDefault() : null; half ? sideMove.emit('right') : null">
      <i class="fas fa-grip-dots-vertical" aria-hidden="true"></i>
    </span>
  `,
  styles: [`
    /* No box of its own, so the grip is placed against the card rather than inside its title row. */
    :host {
      display: contents;
    }

    /* Outside the card, in the gutter to its left: 24px of the deck's own padding beside the first
       column, the 30px between two halves beside the second. The card is the positioned box, and
       its title row starts at its 16px padding, so the grip sits level with the title. */
    .block-grip {
      position: absolute;
      top: 14px;
      left: -20px;
      width: 16px;
      height: 24px;
      border-radius: 4px;
      color: var(--nav-grey3);
      line-height: 1;
      cursor: grab;
    }
    .block-grip:hover,
    .block-grip:focus-visible {
      background-color: var(--nav-grey1);
      color: var(--nav-secondary);
    }
    .block-grip:active {
      cursor: grabbing;
    }
  `],
})
export class ReportBlockActionsComponent {
  /** What the module is called in the grip's accessible name. */
  @Input({ required: true }) label!: string;
  /** The module's id, so the deck can put focus back on this grip after a move redraws the page. */
  @Input({ required: true }) blockId!: string;
  @Input() canMoveUp = false;
  @Input() canMoveDown = false;
  /** Whether the card is half width, which is when the left and right arrow keys move it across. */
  @Input() half = false;

  /** One row up or down, from the arrow keys. */
  @Output() move = new EventEmitter<-1 | 1>();
  /** To that side of its row, from the left and right arrow keys on a half. */
  @Output() sideMove = new EventEmitter<'left' | 'right'>();
  @Output() dragStart = new EventEmitter<DragEvent>();
  @Output() dragEnd = new EventEmitter<void>();
}
